import { createLogic } from "redux-logic";
import * as DOMUtils from "history/DOMUtils";
import * as History from "../../../../../common-deprecated/History";
import Debug from "../../../../../common-deprecated/Debug";
import type { CarConfigLogicType } from "../store";
import { addNotification } from "../../../../../common-deprecated/redux/actions/NotificationActions";
import {
    finishFinanceRateFetch,
    resetFinanceRates,
    resetQuotes,
    setFinanceProductType,
    startFinanceRateFetch,
    updateFinanceQuote,
    updateInsuranceQuote,
} from "../slices/CarConfigFinanceSlice";
import { showAemInsuranceRates } from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { startConfigSave } from "../slices/CarConfigSaveSlice";
import {
    clearInsuranceRate,
    finishInsuranceFetch,
    setInsurancePersonalized,
    startInsuranceRateFetch,
} from "../slices/CarConfigInsuranceSlice";
import { getExternalConfigurationObject } from "../../utils/typedUtils";
import type { MakolabInsuranceResponseType } from "../../../../shared-logic/types/FinanceInsuranceRateTypes";
import { formatInsurance } from "../../../../shared-logic/utils/formatters/formatInsurance";
import {
    getSessionStorageParams,
    setFinanceQuoteIdToStorage,
    setInsuranceQuoteIdToStorage,
} from "../../utils/sessionStorage";
import { calculatePrice, setMonthlyPrices as setBasketMonthlyPrices } from "../slices/CarConfigBasketSlice";
import { fetchCCSaveFinancePayload } from "../../../../shared-logic/utils/saveUtils";
import { EnvironmentEnum, isRetailer } from "../../../../../common-deprecated/utils";
import { showIntegratedTradeIn } from "../../utils/carConfigSettingsUtils";
import { getTradeInNotificationConfig } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtils";
import { setTradeInNotification } from "../../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { fetchMonthlyRate, showCarConfigMonthlyRate } from "../../utils/financeInsuranceRate";
import CarDataConsolidator from "../../../../shared-logic/utils/classes/CarDataConsolidator";
import { setMonthlyPrices as setItemsMonthlyPrices } from "../slices/CarConfigItemSlice";
import type { LegalTextResponseType } from "../../../../shared-logic/types/FinanceTypes";
import { CarConfigConfigureSection } from "../../utils/types/SectionTypes";
import { getCarConfigLabel } from "../../utils/labelUtils";
import { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";
import { setFinanceOption } from "../../../../../common-deprecated/redux/actions/UtilActions";

const updateFinanceQuoteLogic = (createLogic as CarConfigLogicType<ReturnType<typeof updateFinanceQuote>>)({
    type: updateFinanceQuote.type,

    // Disabled consistent return because returning void will break the code
    /* eslint-disable consistent-return */
    validate({ getState, action }, allow, reject) {
        const { finance, commonSettings, car } = getState();
        const { quotes, info } = finance;

        // Quote configs are only relevant in front-end
        if (!DOMUtils.canUseDOM) {
            return (reject as () => void)();
        }

        // Get the necessary settings to determine if finance/insurance quotes should be updated.
        const updateFinanceRates = showCarConfigMonthlyRate(commonSettings, car);

        const financeData = action.payload.finance;

        if (financeData) {
            // If the quotes are the same; do nothing.
            if (financeData === quotes.finance || !updateFinanceRates) {
                // Quotes might be the same but productName might differ, in this case still allow the action.
                if (action.payload?.productName ? info.productName === action.payload.productName : true)
                    return (reject as () => void)();
            }

            action.payload = { ...action.payload, finance: quotes.finance };
            if (financeData && updateFinanceRates) action.payload.finance = financeData;
        } else {
            action.payload = { finance: null };
        }

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        const { commonSettings, carConfigSettings, car, finance } = getState();
        const { quotes } = finance;

        // Get the necessary settings to determine if finance/insurance quotes should be updated.
        const updateFinanceRates = showCarConfigMonthlyRate(commonSettings, car);
        const sessionStorageParams = getSessionStorageParams(getState());

        // Set the finance quotes in the session storage
        if (action.payload.finance) {
            setFinanceQuoteIdToStorage(sessionStorageParams, action.payload.finance, car.model.id);
        }

        // If updateFinanceQuoteLogic is triggered and returns a finance quote...
        // We need to remove the financeProductType from the Redux store because this would overwrite the financeData.
        if (quotes.finance) {
            dispatch(setFinanceProductType({ financeProduct: "" }));
        }

        // If externalPriceCalculation is enabled and we are setting the quotes for the first time
        // we'll load the external price function after the quotes have been set.
        // In this case monthly rate gets fetched in the calculateTotalPrice, triggered when the external price function is correctly set.
        if (carConfigSettings.enableExternalPrice) {
            dispatch(calculatePrice({ loadExternalPrice: true }));
        } else {
            dispatch(startConfigSave());

            // Reload the monthly rates as per usual.
            if (updateFinanceRates) {
                const [financeNotificationTitle, financeNotificationDescription] = getCarConfigLabel(
                    { commonSettings, carConfigSettings },
                    ["carConfigFinanceNotificationTitle", "carConfigFinanceNotificationDescription"],
                );

                dispatch(startFinanceRateFetch({ trigger: FinanceUpdateTrigger.NewQuote }));
                if (action.payload?.finance && !action.payload.hideNotification) {
                    dispatch(
                        addNotification([
                            {
                                title: financeNotificationTitle,
                                description: financeNotificationDescription,
                                action: { closeButton: true },
                            },
                        ]),
                    );
                }
            }
        }

        done();
    },
});

const updateInsuranceQuoteLogic = (createLogic as CarConfigLogicType<ReturnType<typeof updateInsuranceQuote>>)({
    type: updateInsuranceQuote.type,

    validate({ getState, action }, allow, reject): void {
        const { finance, commonSettings, car } = getState();
        const { quotes } = finance;

        // Quote configs are only relevant in front-end
        if (!DOMUtils.canUseDOM) {
            return (reject as () => void)();
        }

        // Get the necessary settings to determine if finance/insurance quotes should be updated.
        const updateInsuranceRates = showAemInsuranceRates(
            commonSettings,
            AemFmComponent.Carconfig,
            "new",
            car.model.internalCode,
        );

        const insuranceData = action.payload.insurance;
        if (insuranceData) {
            // If the quotes are the same; do nothing.
            if (insuranceData === quotes.insurance || !updateInsuranceRates) {
                return (reject as () => void)();
            }

            action.payload = { ...action.payload, insurance: quotes.insurance };
            if (insuranceData && updateInsuranceRates) action.payload.insurance = insuranceData;
        } else {
            action.payload = { insurance: null };
        }

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        const { commonSettings, carConfigSettings, car } = getState();

        // Get the necessary settings to determine if finance/insurance quotes should be updated.
        const updateInsuranceRates = showAemInsuranceRates(
            commonSettings,
            AemFmComponent.Carconfig,
            "new",
            car.model.internalCode,
        );

        const sessionStorageParams = getSessionStorageParams(getState());

        // Set the finance quotes in the session storage
        if (action.payload.insurance) {
            setInsuranceQuoteIdToStorage(sessionStorageParams, action.payload.insurance, car.model.id);
        }

        // If externalPriceCalculation is enabled and we are setting the quotes for the first time
        // we'll load the external price function after the quotes have been set.
        // In this case monthly rate gets fetched in the calculateTotalPrice, triggered when the external price function is correctly set.
        if (carConfigSettings.enableExternalPrice) {
            dispatch(calculatePrice({ loadExternalPrice: true }));
        } else {
            // Execute the save if we're on the summary!
            // This will only work on the summary step in v1 but you cannot change financing from summary in V2 so it is fine
            dispatch(startConfigSave());

            if (updateInsuranceRates) {
                const [insuranceNotificationTitle, insuranceNotificationDescription] = getCarConfigLabel(
                    { commonSettings, carConfigSettings },
                    ["carConfigInsuranceNotificationTitle", "carConfigInsuranceNotificationDescription"],
                );

                dispatch(startInsuranceRateFetch());
                if (action.payload?.insurance && !action.payload.hideNotification) {
                    dispatch(
                        addNotification([
                            {
                                title: insuranceNotificationTitle,
                                description: insuranceNotificationDescription,
                            },
                        ]),
                    );
                }
            }
        }

        done();
    },
});

const resetQuoteLogic = (createLogic as CarConfigLogicType<ReturnType<typeof resetQuotes>>)({
    type: resetQuotes.type,
    process({ getState }, _, done) {
        const state = getState();
        const { car } = state;
        // When resetting the quotes make sure that also the session storage is cleared.
        const sessionStorageParams = getSessionStorageParams(state);
        setFinanceQuoteIdToStorage(sessionStorageParams, null, car.model.id);
        setInsuranceQuoteIdToStorage(sessionStorageParams, null, car.model.id);
        done();
    },
});

const startFinanceFetch = (createLogic as CarConfigLogicType<ReturnType<typeof startFinanceRateFetch>>)({
    type: startFinanceRateFetch.type,

    latest: true,
    validate({ action, getState }, allow, reject) {
        // In retailer-toyota we only show a single finance quote on the overview
        const isToyotaRetailer = isRetailer(getState().commonSettings) && getState().commonSettings.brand === "toyota";
        const isOverview = getState().carConfigNavigation.activeSection === CarConfigConfigureSection.Summary;

        if (
            (!DOMUtils.canUseDOM && !getState().car.initialized && !getState().items.initialized) ||
            (isToyotaRetailer && !isOverview)
        ) {
            (reject as () => void)();
        } else {
            allow(action);
        }
    },

    async process({ action, getState }, dispatch, done) {
        try {
            const { basket, items, commonSettings, carConfigSettings } = getState();
            const config = getExternalConfigurationObject(getState());

            const savingConfig = await fetchCCSaveFinancePayload(carConfigSettings.loadSaveServer, config);

            if (!savingConfig) throw new Error("Something went wrong with the finance payload");

            const allExtras = CarDataConsolidator.getAllRelevantBasketExtras(basket, items);

            const selectedItems = [basket.wheel.id, basket.upholstery.id, basket.colour.id]
                .concat(allExtras.filter((extra) => extra.type === "option").map((extra) => extra.id))
                .concat(allExtras.filter((extra) => extra.type === "accessory").map((extra) => extra.id))
                .concat(basket.packs.map((pack) => pack.id));

            const unSelectedItems = CarDataConsolidator.getUnselectedItemIds(basket, items).concat(
                items.winterWheels.map((winterWheel) => winterWheel.id),
            );

            const result = await fetchMonthlyRate(
                commonSettings,
                carConfigSettings,
                { ...savingConfig, ProductType: config.ProductType },
                selectedItems,
                unSelectedItems,
            );

            if (result) {
                const newRates: Record<string, string> = {};
                result.unselectedItemPrices.forEach((rate) => {
                    newRates[rate.id] = rate.value;
                });
                result.selectedItemPrices.forEach((rate) => {
                    newRates[rate.id] = rate.value;
                });

                dispatch(finishFinanceRateFetch({ result, trigger: action.payload.trigger }));
                dispatch(setItemsMonthlyPrices(newRates));
                dispatch(setBasketMonthlyPrices(newRates));
            }
        } catch (err) {
            dispatch(resetFinanceRates());
            dispatch(setItemsMonthlyPrices({}));
            dispatch(setBasketMonthlyPrices({}));
            dispatch(setFinanceOption("cash"));
            const { commonSettings, carConfigSettings } = getState();

            const [carConfigFinanceErrorNotificationTitle, carConfigFinanceErrorNotificationDescription] =
                getCarConfigLabel({ commonSettings, carConfigSettings }, [
                    "carConfigFinanceErrorNotificationTitle",
                    "carConfigFinanceErrorNotificationDescription",
                ]);
            dispatch(
                addNotification([
                    {
                        title: carConfigFinanceErrorNotificationTitle,
                        description: carConfigFinanceErrorNotificationDescription,
                        action: { closeButton: true },
                    },
                ]),
            );

            Debug.error("Failed to fetch monthly rate", err);
        }
        done();
    },
});

// Not sure if this is really necessary?
const finishFinanceFetch = (createLogic as CarConfigLogicType<ReturnType<typeof finishFinanceRateFetch>>)({
    type: finishFinanceRateFetch.type,

    transform({ getState, action }, next) {
        const { finance } = getState();

        action.payload.previousDownPayment = finance.quoteInfo.DOWNPAYMENT
            ? Number(finance.quoteInfo.DOWNPAYMENT.value)
            : 0;

        next(action);
    },

    async process({ getState, action }, dispatch, done) {
        const { finance, carConfigSettings, commonSettings, tradeInAemExternalData, utils, notification } = getState();
        const { quoteInfo } = action.payload.result;
        const { msgCode } = action.payload.result.info;

        // If there is no valid response from the monthly rate service, toggle to cash!
        if (Object.keys(finance.quoteInfo).length === 0 || finance.error) {
            Debug.warn("ccGetMonthlyPayment returned with empty response, hiding monthly rate tab.");
            dispatch(setFinanceOption("cash"));
            dispatch(resetFinanceRates());
            const [carConfigFinanceErrorNotificationTitle, carConfigFinanceErrorNotificationDescription] =
                getCarConfigLabel({ commonSettings, carConfigSettings }, [
                    "carConfigFinanceErrorNotificationTitle",
                    "carConfigFinanceErrorNotificationDescription",
                ]);
            dispatch(
                addNotification([
                    {
                        title: carConfigFinanceErrorNotificationTitle,
                        description: carConfigFinanceErrorNotificationDescription,
                        action: { closeButton: true },
                    },
                ]),
            );
        }

        // Trade-in finance integration: Check if a notification should be shown.
        if (showIntegratedTradeIn(carConfigSettings, commonSettings, utils.financeOption, finance.info.productName)) {
            let show = false;
            let notificationType;
            let isError;

            if (msgCode) {
                const pxTitle = getCarConfigLabel(
                    { commonSettings, carConfigSettings },
                    "carConfigTradeinNotificationUpdate",
                );
                const pxNotificationActive = !!notification?.notifications.find(({ title }) => title === pxTitle);

                // Don't show notification if there is already a px notification active.
                if (!pxNotificationActive) {
                    // Check if there is a difference in deposit.
                    const nextDownPayment = quoteInfo?.DOWNPAYMENT ? Number(quoteInfo.DOWNPAYMENT.value) : 0;
                    const depositDiff = nextDownPayment - (action.payload.previousDownPayment || 0);

                    ({ notificationType, isError, show } = getTradeInNotificationConfig(
                        msgCode,
                        action.payload.trigger,
                        depositDiff,
                        !(commonSettings.environment === EnvironmentEnum.Production),
                        tradeInAemExternalData.notification.monthly.notificationType,
                        tradeInAemExternalData.notification.monthly.show,
                        // The AllowTradeInProduct functionality looks not used in Build And Buy at the moment so wasn't migrated to Apheleia.
                        // This can be re-added later if needed.
                        "",
                        finance.info.productName,
                    ));
                }

                dispatch(setTradeInNotification("monthly", show, notificationType, isError));
            }
        }
        done();
    },
});

const updateFinanceProductType = (createLogic as CarConfigLogicType<ReturnType<typeof setFinanceProductType>>)({
    type: setFinanceProductType.type,

    async process({ action, getState }, _, done) {
        const { financeProduct } = action.payload;

        const sessionStorageParams = getSessionStorageParams(getState());

        // When there is a financeProductType availaible and there is already a finance quote in the storage...
        // We remove that quote from the sessions storage. This way the custom finance data is overwritten by the given productType query.
        if (!!financeProduct && !!sessionStorageParams) {
            setFinanceQuoteIdToStorage(sessionStorageParams, "", "");
        }
        // If the financeProductType state is empty, we remove the query from the url.
        else if (!financeProduct) {
            History.replaceQueryParams({ financeProductType: null });
        }

        done();
    },
});

const startInsuranceFetch = (createLogic as CarConfigLogicType<ReturnType<typeof startInsuranceRateFetch>>)({
    type: startInsuranceRateFetch.type,
    latest: true,

    validate({ action, getState }, allow, reject) {
        const { finance } = getState();
        const isToyotaRetailer = isRetailer(getState().commonSettings) && getState().commonSettings.brand === "toyota";
        if (
            !DOMUtils.canUseDOM ||
            // if there is no quote id do not fetch insurance as you need a quote id to fetch this
            !finance.quotes.insurance ||
            !getState().car.initialized ||
            !getState().items.initialized ||
            isToyotaRetailer // insurance fully disabled on retailer-toyota
        ) {
            (reject as () => void)();
        } else {
            allow(action);
        }
    },

    async process({ getState }, dispatch, done) {
        const { carConfigSettings } = getState();

        try {
            const config = getExternalConfigurationObject(getState());

            const savingConfig = await fetchCCSaveFinancePayload(carConfigSettings.loadSaveServer, config);

            const result = await fetch(carConfigSettings.insuranceRate, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(savingConfig),
                mode: "cors",
            });

            const insuranceRate: MakolabInsuranceResponseType = await result.json();

            const legalTextResponse = await fetch(carConfigSettings.insuranceRateDisclaimer, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ...savingConfig,
                    Legal_Text_ID: insuranceRate?.Quotation?.Legal_Text_ID || "",
                    Execution_ID: insuranceRate?.Quotation?.Execution_ID || "",
                }),
            });

            const legalText: LegalTextResponseType = legalTextResponse.ok && (await legalTextResponse.json());

            dispatch(finishInsuranceFetch({ result: formatInsurance(insuranceRate, legalText) }));
        } catch (err) {
            Debug.error("Failed to fetch insurance rate", err);
        }

        done();
    },
});

// Needed?
// const prepareCalculatorLogic = (createLogic as CarConfigLogicType<PrepareCalculatorType>)({
//     type: PREPARE_CALCULATOR,
//
//     validate({ getState, action }, allow, reject) {
//         const { executeSave, loadPSLS } = action;
//         const { save, psls } = getState();
//         if (executeSave && !save.saved) {
//             reject(startConfigSave(action));
//         } else if (loadPSLS && !action.pslsLoaded && !psls.data) {
//             reject(startPSLSFetch(action));
//         } else allow(action);
//     },
// });

const clearInsuranceLogic = (createLogic as CarConfigLogicType<ReturnType<typeof clearInsuranceRate>>)({
    type: clearInsuranceRate.type,

    async process({ getState }, dispatch, done) {
        const state = getState();
        const { car } = state;

        setInsuranceQuoteIdToStorage(getSessionStorageParams(state), null, car.model.id);

        dispatch(setInsurancePersonalized(false));
        return done();
    },
});

export default [
    updateFinanceProductType,
    updateFinanceQuoteLogic,
    updateInsuranceQuoteLogic,
    resetQuoteLogic,
    startFinanceFetch,
    startInsuranceFetch,
    finishFinanceFetch,
    // prepareCalculatorLogic,
    // resetCalculatorLogic,
    clearInsuranceLogic,
];

export const financeInsuranceServerLogic = [startFinanceFetch, startInsuranceFetch];
